export type PredictionSelectionType = 'phrase_completion' | 'token_prediction' | 'shortcut';

export type SelectionStats = {
  count: number;
  lastSelectedAt: number;
  recentSelections: number;
  recentWindowStart: number;
};

export type SelectionMap = Record<string, SelectionStats>;

const BASE_STORAGE_KEY = 'ecovoz:prediction-selections';
const SELECTION_BOOST_FACTOR = 0.05;
const MAX_SELECTION_BOOST = 0.25;
const DAY_MS = 86_400_000;
const DECAY_TAU_MS = 30 * DAY_MS;
const BURST_WINDOW_MS = 5 * 60_000;
const BURST_MAX_WEIGHT = 3;
const COOLDOWN_MS = 15_000;
const COOLDOWN_FACTOR = 0.5;

let storageKey = BASE_STORAGE_KEY;

const hasStorage = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

const normalizeLocale = (value: string): string => value.trim().toLowerCase();

function isValidSelectionStats(value: unknown): value is SelectionStats {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const stats = value as Record<string, unknown>;
  return typeof stats.count === 'number' && typeof stats.lastSelectedAt === 'number';
}

function toSelectionStats(value: unknown): SelectionStats | null {
  if (!isValidSelectionStats(value)) {
    return null;
  }

  const stats = value as Record<string, unknown>;

  return {
    count: Math.max(0, stats.count as number),
    lastSelectedAt: stats.lastSelectedAt as number,
    recentSelections: typeof stats.recentSelections === 'number'
      ? Math.max(0, stats.recentSelections)
      : 0,
    recentWindowStart: typeof stats.recentWindowStart === 'number'
      ? stats.recentWindowStart
      : 0,
  };
}

function loadFromStorage(): SelectionMap {
  if (!hasStorage()) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    const map: SelectionMap = {};
    for (const [key, value] of Object.entries(parsed)) {
      const stats = toSelectionStats(value);
      if (stats) {
        map[key] = stats;
      }
    }

    return map;
  } catch {
    return {};
  }
}

function saveToStorage(map: SelectionMap): void {
  if (!hasStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    // quota exceeded or localStorage unavailable — fail silently
  }
}

let selectionMap: SelectionMap = loadFromStorage();

export function setPredictionSelectionOwner(userId: string): void {
  storageKey = `${BASE_STORAGE_KEY}:${userId}`;
  selectionMap = loadFromStorage();
}

export function clearPredictionSelectionOwner(): void {
  if (!hasStorage()) {
    selectionMap = {};
    storageKey = BASE_STORAGE_KEY;
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // unavailable — fail silently
  }

  selectionMap = {};
  storageKey = BASE_STORAGE_KEY;
}

export function clearPredictionSelections(): void {
  selectionMap = {};
  saveToStorage(selectionMap);
}

export function getSelectionKey(
  suggestion: string,
  locale: string,
  type: PredictionSelectionType,
): string {
  return `${type}:${normalizeLocale(locale || 'pt')}:${normalizeText(suggestion)}`;
}

export function recordPredictionSelection(
  suggestion: string,
  locale: string,
  type: PredictionSelectionType,
  selectedAt = Date.now(),
): void {
  const key = getSelectionKey(suggestion, locale, type);
  const current = selectionMap[key] ?? {
    count: 0,
    lastSelectedAt: 0,
    recentSelections: 0,
    recentWindowStart: 0,
  };
  const withinBurstWindow = current.recentSelections > 0
    && selectedAt >= current.recentWindowStart
    && (selectedAt - current.recentWindowStart) <= BURST_WINDOW_MS;
  const recentWindowStart = withinBurstWindow ? current.recentWindowStart : selectedAt;
  const recentSelections = withinBurstWindow ? current.recentSelections + 1 : 1;

  selectionMap = {
    ...selectionMap,
    [key]: {
      count: current.count + 1,
      lastSelectedAt: Math.max(current.lastSelectedAt, selectedAt),
      recentSelections,
      recentWindowStart,
    },
  };

  saveToStorage(selectionMap);
}

export function getPredictionSelectionBoost(
  suggestion: string,
  locale: string,
  type: PredictionSelectionType,
  now = Date.now(),
): number {
  const key = getSelectionKey(suggestion, locale, type);
  const stats = selectionMap[key];
  if (!stats) {
    return 0;
  }

  const burstCount = Math.max(0, stats.recentSelections);
  const adjustedCount = Math.max(
    0,
    stats.count - burstCount + Math.min(burstCount, BURST_MAX_WEIGHT),
  );
  const baseBoost = Math.min(Math.log1p(adjustedCount) * SELECTION_BOOST_FACTOR, MAX_SELECTION_BOOST);
  const ageMs = Math.max(0, now - stats.lastSelectedAt);
  const decay = Math.exp(-ageMs / DECAY_TAU_MS);
  const cooldownFactor = ageMs < COOLDOWN_MS ? COOLDOWN_FACTOR : 1;

  return baseBoost * decay * cooldownFactor;
}
